import { useEffect, useMemo, useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { supabase } from "../lib/supabase";
import { logChannelStatus } from "../lib/realtimeStatus";
import { fmtK } from "../lib/money";
import { resolveImage } from "../lib/images";
import { timeAgo } from "../lib/dateUtils";

function firstItemImage(o) {
  const items = o?.order_items || [];
  const first = items[0];
  return resolveImage(first?.products?.images, "thumb") || first?.product_image || null;
}

// Kanban lanes map 1:1 to sub_order status values. "new" = confirmed (just paid).
const LANES = [
  { id: "confirmed", label: "New orders", tone: "amber", next: "preparing", nextLabel: "Start preparing" },
  { id: "preparing", label: "Preparing", tone: "hunter", next: "shipped", nextLabel: "Mark shipped" },
  { id: "shipped", label: "Out for delivery", tone: "blue", next: "delivered", nextLabel: "Mark delivered" },
  { id: "delivered", label: "Delivered", tone: "greenDk", next: null, nextLabel: null },
  { id: "completed", label: "Completed", tone: "muted", next: null, nextLabel: null },
];

const LANE_BG = {
  amber: { bg: C.amberBg, fg: C.amberDk },
  hunter: { bg: C.cream, fg: C.hunter },
  blue: { bg: C.blueBg, fg: C.blueDk },
  greenDk: { bg: C.greenBg, fg: C.greenDk },
  muted: { bg: C.cream, fg: C.muted },
};

const STATUS_TIMESTAMPS = {
  preparing: null, // no column — only created_at/updated_at
  shipped: "shipped_at",
  delivered: "delivered_at",
  completed: "completed_at",
};

export function SellerOrders({ seller, openSubOrderId, onOpenedSubOrder, onMessageBuyer }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  // Deep-link from a chat order-ref card. Open the matching order's drawer
  // once orders have loaded and the row is in our list.
  useEffect(() => {
    if (!openSubOrderId || !orders.length) return;
    const match = orders.find((o) => o.id === openSubOrderId);
    if (match) {
      setSelected(match);
      onOpenedSubOrder?.();
    }
  }, [openSubOrderId, orders, onOpenedSubOrder]);

  useEffect(() => {
    if (!seller?.id) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      // `orders!inner(...)` would drop sub_orders whose parent row isn't visible
      // via RLS — use a left join + separately hydrate buyer names. Drop the
      // 30-day window: a seller's open board can include orders older than that
      // (e.g. a 45-day-old "preparing" order still needs action).
      const { data: subs, error } = await supabase
        .from("sub_orders")
        .select(
          "id, order_id, sub_number, total, subtotal, delivery_fee, status, delivery_method, delivery_address, created_at, shipped_at, delivered_at, orders(order_number, buyer_id), order_items(id, product_name, product_image, quantity, unit_price, size, products(title, images))"
        )
        .eq("seller_id", seller.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (cancelled) return;
      if (error) {
        console.error("[SellerOrders] sub_orders query failed", error);
        setOrders([]);
        setLoading(false);
        return;
      }
      // Second pass: fetch buyer profiles for all the parent orders we saw.
      const buyerIds = [
        ...new Set((subs || []).map((s) => s.orders?.buyer_id).filter(Boolean)),
      ];
      let buyersById = {};
      if (buyerIds.length > 0) {
        const { data: buyers } = await supabase
          .from("users")
          .select("id, name, phone, avatar_url")
          .in("id", buyerIds);
        buyersById = Object.fromEntries((buyers || []).map((u) => [u.id, u]));
      }
      const hydrated = (subs || []).map((s) => ({
        ...s,
        buyer: s.orders?.buyer_id ? buyersById[s.orders.buyer_id] || null : null,
      }));
      if (cancelled) return;
      setOrders(hydrated);
      setLoading(false);
    }
    load();
    // Realtime: new paid orders + status flips by other sessions.
    const channelName = `seller-orders:${seller.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sub_orders", filter: `seller_id=eq.${seller.id}` },
        () => load()
      )
      .subscribe(logChannelStatus(channelName));
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [seller?.id]);

  const byLane = useMemo(() => {
    const buckets = Object.fromEntries(LANES.map((l) => [l.id, []]));
    orders.forEach((o) => {
      // pending_payment is hidden — seller shouldn't act on unpaid orders.
      // cancelled/refunded roll up into Completed lane as terminal states.
      if (o.status === "pending_payment") return;
      if (buckets[o.status]) buckets[o.status].push(o);
      else if (o.status === "cancelled" || o.status === "refunded") buckets.completed.push(o);
    });
    return buckets;
  }, [orders]);

  async function advance(order, nextStatus) {
    setSaving(true);
    const patch = { status: nextStatus };
    const col = STATUS_TIMESTAMPS[nextStatus];
    if (col) patch[col] = new Date().toISOString();
    const { error } = await supabase.from("sub_orders").update(patch).eq("id", order.id);
    setSaving(false);
    if (!error) {
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...patch } : o)));
      setSelected((s) => (s?.id === order.id ? { ...s, ...patch } : s));
    }
  }

  if (!seller) {
    return <EmptyState icon="store" title="No seller account" body="Seller orders require a seller profile." />;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <Eyebrow>Orders</Eyebrow>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: C.charcoal, margin: 0, letterSpacing: "-0.5px" }}>
            Order board
          </h1>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>
            {orders.length} {orders.length === 1 ? "order" : "orders"}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={360} />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
          {LANES.map((lane) => (
            <Lane
              key={lane.id}
              lane={lane}
              orders={byLane[lane.id] || []}
              onOpen={setSelected}
              onAdvance={advance}
              saving={saving}
            />
          ))}
        </div>
      )}

      {selected && (
        <OrderDrawer
          order={selected}
          sellerId={seller?.id}
          onClose={() => setSelected(null)}
          onAdvance={advance}
          saving={saving}
          onMessageBuyer={onMessageBuyer}
        />
      )}
    </div>
  );
}

function Lane({ lane, orders, onOpen, onAdvance, saving }) {
  const tone = LANE_BG[lane.tone] || LANE_BG.hunter;
  return (
    <div
      style={{
        background: C.cream,
        borderRadius: 14,
        padding: 10,
        minHeight: 360,
        border: "1px solid rgba(196,193,183,0.3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 6px 10px" }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            padding: "3px 8px",
            borderRadius: 8,
            background: tone.bg,
            color: tone.fg,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          {lane.label}
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: C.muted }}>{orders.length}</span>
      </div>
      {orders.length === 0 ? (
        <div style={{ fontSize: 11.5, color: C.muted, padding: 12, textAlign: "center" }}>Empty</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {orders.map((o) => (
            <LaneCard key={o.id} order={o} lane={lane} onOpen={onOpen} onAdvance={onAdvance} saving={saving} />
          ))}
        </div>
      )}
    </div>
  );
}

function LaneCard({ order, lane, onOpen, onAdvance, saving }) {
  const items = order.order_items || [];
  const itemCount = items.reduce((s, i) => s + (i.quantity || 0), 0);
  // Sub-orders should always have at least one line item. An empty array here
  // indicates an orphaned record (checkout failed mid-flow before the items
  // insert) or RLS blocking the join. Show a clear "needs review" label
  // instead of "0 items" so sellers don't think the customer ordered nothing.
  const itemLabel = items.length === 0
    ? "items unavailable — contact support"
    : `${itemCount} ${itemCount === 1 ? "item" : "items"}`;
  return (
    <div
      onClick={() => onOpen(order)}
      style={{
        background: C.white,
        borderRadius: 10,
        padding: 12,
        cursor: "pointer",
        border: "1px solid rgba(196,193,183,0.25)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: C.charcoal }}>{order.sub_number}</span>
        <span style={{ fontSize: 10.5, color: C.muted }}>{timeAgo(order.created_at)}</span>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.charcoal }}>
        {order.buyer?.name || "Buyer"}
      </div>
      <div style={{ fontSize: 11, color: items.length === 0 ? C.red : C.muted, marginBottom: 8 }}>
        {itemLabel} · {order.delivery_method}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 900, color: C.hunter }}>{fmtK(order.total)}</span>
        {lane.next && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAdvance(order, lane.next);
            }}
            disabled={saving}
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: C.hunter,
              background: "transparent",
              border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              padding: 0,
            }}
          >
            {lane.nextLabel} →
          </button>
        )}
      </div>
    </div>
  );
}

function OrderDrawer({ order, sellerId, onClose, onAdvance, saving, onMessageBuyer }) {
  const lane = LANES.find((l) => l.id === order.status);

  // The `users` table is not selectable by sellers via RLS, so pull buyer +
  // delivery address through the same SECURITY DEFINER RPC the mobile app uses
  // (`get_buyer_for_order`). Mirror the RPC's JSON shape: buyer columns at the
  // top level, and a nested `delivery_address` object from the delivery_addresses
  // row referenced by the parent order.
  const [buyer, setBuyer] = useState(null);
  const [stats, setStats] = useState(null);
  const [buyerLoading, setBuyerLoading] = useState(true);

  useEffect(() => {
    if (!order?.id) return;
    let cancelled = false;
    setBuyerLoading(true);
    setBuyer(null);
    setStats(null);
    (async () => {
      const { data } = await supabase.rpc("get_buyer_for_order", { p_sub_order_id: order.id });
      if (cancelled) return;
      setBuyer(data || null);
      setBuyerLoading(false);
      if (data?.id && sellerId) {
        const { data: s } = await supabase.rpc("get_buyer_order_stats", {
          p_buyer_id: data.id,
          p_seller_id: sellerId,
        });
        if (!cancelled) setStats(s || null);
      }
    })();
    return () => { cancelled = true; };
  }, [order?.id, sellerId]);

  const deliveryAddr = buyer?.delivery_address;
  const recipient = deliveryAddr?.recipient_name || buyer?.name;
  const deliveryPhone = deliveryAddr?.phone || buyer?.phone;
  // Prefer the linked delivery_addresses row. Fall back to the denormalized
  // `delivery_address` text on sub_orders (used for pickup/self orders that
  // don't reference a saved address).
  const streetLine = deliveryAddr
    ? [deliveryAddr.street_address, deliveryAddr.city].filter(Boolean).join(", ")
    : order.delivery_address || null;
  const memberSince = buyer?.created_at
    ? new Date(buyer.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : null;
  const methodLabel = ({ pickup: "Pickup", self: "Self delivery", courier: "Courier" })[order.delivery_method] || order.delivery_method;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.35)",
        zIndex: 100,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 460,
          height: "100%",
          background: C.bg,
          overflowY: "auto",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        }}
      >
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid rgba(196,193,183,0.3)",
            background: C.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <div>
            <Eyebrow>{order.sub_number}</Eyebrow>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.charcoal }}>
              {buyer?.name || order.buyer?.name || "Buyer"}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
          >
            <Icon name="x" size={20} color={C.charcoal} />
          </button>
        </div>

        <div style={{ padding: "20px 22px", display: "grid", gap: 14 }}>
          <Card pad={14}>
            <Eyebrow>Status</Eyebrow>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.charcoal, marginBottom: 10 }}>
              {lane?.label || order.status}
            </div>
            {lane?.next && (
              <Btn onClick={() => onAdvance(order, lane.next)} disabled={saving} full>
                {lane.nextLabel}
              </Btn>
            )}
          </Card>

          <Card pad={14}>
            <Eyebrow>Items</Eyebrow>
            <div style={{ display: "grid", gap: 10 }}>
              {(order.order_items || []).map((it) => {
                const thumb = resolveImage(it.products?.images, "thumb");
                return (
                <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: thumb ? `url(${thumb}) center/cover` : C.cream,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {!thumb && <Icon name="package" size={16} color={C.muted} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: C.charcoal }}>
                      {it.products?.title || it.product_name || "Product"}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>
                      Qty {it.quantity}{it.size ? ` · Size ${it.size}` : ""}
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: C.charcoal }}>
                    {fmtK((it.unit_price || 0) * (it.quantity || 1))}
                  </div>
                </div>
                );
              })}
            </div>
            <div style={{ borderTop: `1px solid ${C.cream}`, marginTop: 12, paddingTop: 12, display: "grid", gap: 4 }}>
              <Row label="Subtotal" value={fmtK(order.subtotal)} />
              <Row label="Delivery" value={fmtK(order.delivery_fee)} />
              <Row label="Total" value={fmtK(order.total)} bold />
            </div>
          </Card>

          <Card pad={14}>
            <Eyebrow>Delivery</Eyebrow>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Icon name={order.delivery_method === "pickup" ? "map-pin" : order.delivery_method === "courier" ? "package" : "truck"} size={14} color={C.hunter} />
              <span style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>{methodLabel}</span>
              {deliveryAddr?.label && (
                <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 6, background: C.cream, color: C.slate, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>
                  {deliveryAddr.label}
                </span>
              )}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {recipient && (
                <DrawerLine icon="user" value={recipient} sub="Recipient" />
              )}
              {deliveryPhone && (
                <DrawerLine icon="smartphone" value={deliveryPhone} sub="Contact" />
              )}
              {streetLine && (
                <DrawerLine icon="map-pin" value={streetLine} sub="Address" />
              )}
              {!recipient && !deliveryPhone && !streetLine && !buyerLoading && (
                <div style={{ fontSize: 11.5, color: C.muted }}>No delivery details on file.</div>
              )}
            </div>
          </Card>

          <Card pad={14}>
            <Eyebrow>Buyer</Eyebrow>
            {buyerLoading ? (
              <Skeleton h={48} />
            ) : buyer ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: buyer.avatar_url ? `url(${buyer.avatar_url}) center/cover` : C.hunter,
                    color: C.dust,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 15, fontWeight: 900, flexShrink: 0,
                  }}>
                    {!buyer.avatar_url && (buyer.name?.[0] || "?").toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: C.charcoal }}>{buyer.name || "—"}</div>
                    {memberSince && (
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Member since {memberSince}</div>
                    )}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  {buyer.email && <DrawerLine icon="mail" value={buyer.email} />}
                  {buyer.phone && <DrawerLine icon="smartphone" value={buyer.phone} />}
                </div>
                {stats && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.cream}` }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Orders with you</div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: C.charcoal, marginTop: 2 }}>{stats.order_count ?? 0}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Total spent</div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: C.charcoal, marginTop: 2 }}>{fmtK(stats.total_spent ?? 0)}</div>
                    </div>
                  </div>
                )}
                {buyer.id && (
                  <div style={{ marginTop: 12 }}>
                    <Btn
                      kind="outline"
                      icon="message-square"
                      full
                      onClick={() =>
                        onMessageBuyer?.(buyer.id, {
                          sub_order_id: order.id,
                          sub_number: order.sub_number,
                          parent_order_number: order.orders?.order_number,
                          image: firstItemImage(order),
                          status: order.status,
                        })
                      }
                    >
                      Message customer
                    </Btn>
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: C.muted }}>Buyer info unavailable.</div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
      <span style={{ fontSize: bold ? 14 : 12.5, fontWeight: bold ? 900 : 700, color: C.charcoal }}>{value}</span>
    </div>
  );
}

function DrawerLine({ icon, value, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <Icon name={icon} size={13} color={C.muted} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {sub && (
          <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 1 }}>
            {sub}
          </div>
        )}
        <div style={{ fontSize: 12.5, color: C.charcoal, fontWeight: 600, wordBreak: "break-word" }}>{value}</div>
      </div>
    </div>
  );
}
